import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { dailyBalanceService } from '../../services/api';
import { getLocale, availableLanguages } from '../../locales';
import BirthDataForm from '../vedic/forms/BirthDataForm';
import { to24Hour } from '../vedic/forms/TimeInput';
import ResultTabs from '../vedic/results/ResultTabs';
import SharedScoreRing from '../vedic/results/ScoreRing';
import LoadingState from '../vedic/feedback/LoadingState';
import ResponsiveTable from '../vedic/results/ResponsiveTable';

/* ─── Animations ─── */
const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;

/* ─── Styled Components ─── */
const PageContainer = styled.div`max-width:1200px;margin:40px auto;padding:0 20px;`;
const PageTitle = styled.h1`font-size:2rem;text-align:center;margin-bottom:5px;color:${p => p.theme.colors.text.primary};`;
const PageSubtitle = styled.p`text-align:center;color:${p => p.theme.colors.text.secondary};margin-bottom:30px;font-size:0.95rem;`;

const TopGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:30px;
  @media(max-width:992px){grid-template-columns:1fr}
`;

const Card = styled.div`
  background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};padding:30px;
  animation:${fadeIn} 0.3s ease;
`;

const CardTitle = styled.h2`font-size:1.3rem;margin-bottom:20px;color:${p => p.theme.colors.text.primary};text-align:center;`;

const Select = styled.select`
  width:100%;padding:10px 12px;border:1px solid ${p => p.theme.colors.border};border-radius:6px;font-size:0.95rem;
  &:focus{outline:none;border-color:${p => p.theme.colors.primary}}
`;

const SubmitButton = styled.button`
  width:100%;padding:12px;background:${p => p.theme.colors.primary};color:${p => p.theme.colors.surface};border:none;border-radius:6px;
  font-size:1rem;font-weight:600;cursor:pointer;margin-top:10px;
  &:hover{background:${p => p.theme.colors.primary}}&:disabled{opacity:0.6;cursor:not-allowed}
`;


const Summary = styled.p`text-align:center;color:${p => p.theme.colors.text.secondary};font-size:1rem;margin:10px 0 20px;`;

/* ─── Components Bar ─── */
const ComponentsBar = styled.div`display:flex;gap:10px;justify-content:center;flex-wrap:wrap;margin-bottom:20px;`;
const ComponentChip = styled.div`
  background:${p=>p.bg||'#f0f0ff'};padding:8px 14px;border-radius:20px;font-size:0.8rem;
  font-weight:600;color:${p=>p.fg||p.theme.colors.primary};
`;

/* ─── Highlights ─── */
const HighlightCard = styled.div`
  border-left:4px solid ${p=>p.type==='strength'?p.theme.colors.bands.strong:p.theme.colors.bands.weak};
  background:${p=>p.type==='strength'?'#f1f8e9':'#fff8e1'};
  padding:16px;border-radius:0 8px 8px 0;margin-bottom:12px;animation:${fadeIn} 0.4s ease;
`;
const HighlightType = styled.span`
  font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:1px;
  color:${p=>p.type==='strength'?'#2e7d32':'#e65100'};
`;
const HighlightHeadline = styled.div`font-weight:600;font-size:1rem;margin:4px 0;color:${p => p.theme.colors.text.primary};`;
const HighlightDetail = styled.div`font-size:0.85rem;color:${p => p.theme.colors.text.secondary};line-height:1.5;`;
const HighlightMeta = styled.div`font-size:0.75rem;color:${p => p.theme.colors.text.muted};margin-top:6px;`;

/* ─── Planet Reports ─── */
const ReportGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:16px;margin-top:20px;
`;
const PlanetCard = styled.div`
  background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};padding:20px;
  border-top:3px solid ${p=>p.effectColor};animation:${fadeIn} 0.3s ease;
`;
const PlanetHeader = styled.div`display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;`;
const PlanetName = styled.span`font-weight:700;font-size:1.1rem;`;
const PlanetTamil = styled.span`font-size:0.85rem;color:${p => p.theme.colors.text.muted};margin-left:6px;`;
const EffectBadge = styled.span`
  font-size:0.7rem;font-weight:700;text-transform:uppercase;padding:3px 10px;border-radius: ${p => p.theme.radius.lg}px;
  background:${p=>({positive:'#e8f5e9',neutral:'#e3f2fd',challenging:'#fce4ec',mixed:'#fff3e0'}[p.effect])};
  color:${p=>({positive:'#2e7d32',neutral:'#1565c0',challenging:p.theme.colors.error.text,mixed:'#e65100'}[p.effect])};
`;
const PlanetRow = styled.div`display:flex;justify-content:space-between;font-size:0.85rem;padding:3px 0;color:${p => p.theme.colors.text.secondary};`;
const PlanetSection = styled.div`margin-top:10px;padding-top:10px;border-top:1px solid ${p => p.theme.colors.border};`;
const PlanetHeadline = styled.div`font-weight:600;color:${p => p.theme.colors.text.primary};margin-bottom:4px;`;
const PlanetGuidance = styled.div`font-size:0.85rem;color:${p => p.theme.colors.text.secondary};font-style:italic;`;



/* ─── Hourly Timeline ─── */
const TimelineContainer = styled.div`display:flex;flex-direction:column;gap:2px;margin-top:16px;`;
const TimelineRow = styled.div`
  display:grid;grid-template-columns:60px 1fr 50px;align-items:center;gap:8px;padding:6px 10px;
  background:${p=>p.isBest?'#e8f5e9':p.theme.colors.background};border-radius: ${p => p.theme.radius.sm}px;
  ${p=>p.isBest&&'border-left:3px solid ${p => p.theme.colors.bands.strong};font-weight:600;'}
  cursor:pointer;&:hover{background:${p=>p.isBest?'#c8e6c9':p.theme.colors.border}}
`;
const TimelineBar = styled.div`
  height:14px;background:${p=>p.color};border-radius:7px;width:${p=>p.pct}%;min-width:4px;
`;
const TimelineEvent = styled.div`font-size:0.75rem;color:${p => p.theme.colors.text.muted};grid-column:1/-1;padding-left:68px;`;
const TimelineDetail = styled.div`
  grid-column:1/-1;padding:10px 12px 10px 68px;font-size:0.82rem;color:${p => p.theme.colors.text.secondary};
  background:#f8f8ff;border-radius: ${p => p.theme.radius.sm}px;margin-top:-2px;line-height:1.6;
`;
const PlanetPill = styled.span`
  display:inline-block;padding:2px 8px;border-radius:10px;font-size:0.72rem;font-weight:600;margin:2px 3px;
  background:${p=>p.effect==='strong'?'#e8f5e9':p.effect==='weak'?'#fce4ec':p.theme.colors.background};
  color:${p=>p.effect==='strong'?'#2e7d32':p.effect==='weak'?p.theme.colors.error.text:p.theme.colors.text.secondary};
`;

/* ─── Weekly ─── */
const WeekGrid = styled.div`display:grid;grid-template-columns:repeat(7,1fr);gap:8px;
  @media(max-width:768px){grid-template-columns:repeat(3,1fr)}
  @media(max-width:480px){grid-template-columns:repeat(2,1fr)}
`;
const DayCard = styled.div`
  background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};
  padding:16px 12px;text-align:center;cursor:pointer;
  border-top:3px solid ${p=>p.color};
  ${p=>p.selected&&'outline:2px solid ${p => p.theme.colors.primary};'}
  &:hover{box-shadow: ${p => p.theme.shadow.elevated}}
`;
const DayDate = styled.div`font-size:0.75rem;color:${p => p.theme.colors.text.muted};`;
const DayName = styled.div`font-weight:600;font-size:0.85rem;color:${p => p.theme.colors.text.primary};margin:4px 0;`;
const DayScore = styled.div`font-size:1.8rem;font-weight:700;color:${p=>p.color};`;
const DayBand = styled.div`font-size:0.7rem;text-transform:uppercase;color:${p => p.theme.colors.text.secondary};letter-spacing:1px;`;
const DayMoon = styled.div`font-size:0.7rem;color:${p => p.theme.colors.text.muted};margin-top:4px;`;

/* ─── Projection ─── */
const ProjectionGrid = styled.div`display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin:16px 0;
  @media(max-width:576px){grid-template-columns:1fr}
`;
const PeriodCard = styled.div`
  background:${p=>({strong:'#e8f5e9',moderate:'#fff3e0',low:'#fce4ec'}[p.quality])};
  padding:14px;border-radius: ${p => p.theme.radius.md}px;text-align:center;
`;
const PeriodLabel = styled.div`font-weight:700;font-size:0.85rem;color:${p => p.theme.colors.text.primary};`;
const PeriodScore = styled.div`font-size:1.4rem;font-weight:700;color:${p=>({strong:'#2e7d32',moderate:'#e65100',low:p.theme.colors.error.text}[p.quality])};margin:4px 0;`;
const PeriodQuality = styled.div`font-size:0.75rem;text-transform:uppercase;color:${p => p.theme.colors.text.secondary};`;


/* ─── Component ─── */
const DailyBalance = () => {
  const [lang, setLang] = useState('en');
  const t = getLocale(lang);
  const [formValues, setFormValues] = useState({
    name:'',gender:'male',dob:'',hour:'',minute:'',period:'AM',timeFormat:'24h',
    place:'',latitude:null,longitude:null,timezone:'Asia/Kolkata',ayanamsa:'lahiri'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [hourly, setHourly] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [activeTab, setActiveTab] = useState('highlights');
  const [expandedHour, setExpandedHour] = useState(null);
  const [selectedWeekDay, setSelectedWeekDay] = useState(null);

  const lastPayloadRef = useRef(null);

  const fetchResults = useCallback(async (payload) => {
    setError('');
    setLoading(true);
    try {
      const [balanceRes, hourlyRes, weeklyRes] = await Promise.all([
        dailyBalanceService.getDailyBalance(payload),
        dailyBalanceService.getHourlyTimeline(payload),
        dailyBalanceService.getWeeklyForecast(payload),
      ]);
      setResult(balanceRes.data);
      setHourly(hourlyRes.data);
      setWeekly(weeklyRes.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to compute daily balance');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = useCallback((submitValues) => {
    if (!submitValues.latitude || !submitValues.longitude) {
      setError('Please select a place from the suggestions to get coordinates.');
      return;
    }
    const dobParts = (submitValues.dob || '').split('-');
    if (dobParts.length !== 3) {
      setError('Please enter a valid date of birth.');
      return;
    }
    const hour24 = to24Hour(
      parseInt(submitValues.hour || '0'),
      submitValues.period || 'AM',
      submitValues.timeFormat || '24h'
    );
    const payload = {
      name: submitValues.name, gender: submitValues.gender,
      year: parseInt(dobParts[0]), month: parseInt(dobParts[1]), day: parseInt(dobParts[2]),
      hour: hour24, minute: parseInt(submitValues.minute || '0'),
      place: submitValues.place, latitude: submitValues.latitude, longitude: submitValues.longitude,
      timezone: submitValues.timezone,
      lang: lang,
    };
    lastPayloadRef.current = payload;
    fetchResults(payload);
  }, [lang, fetchResults]);

  // Re-fetch when language changes while results are showing
  useEffect(() => {
    if (result && lastPayloadRef.current) {
      const payload = { ...lastPayloadRef.current, lang: lang };
      lastPayloadRef.current = payload;
      fetchResults(payload);
    }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const scoreColor = result ? (result.color || '#6c63ff') : '#6c63ff';

  return (
    <PageContainer>
      <PageTitle>{t.dailyBalanceTitle || 'KalaShakti Daily Balance'}</PageTitle>
      <PageSubtitle>{t.dailyBalanceSubtitle || 'Your personalized daily planetary strength based on Ashtakavarga'}</PageSubtitle>

      <div style={{textAlign:'right',marginBottom:10}}>
        <Select value={lang} onChange={e=>setLang(e.target.value)} style={{width:'auto',padding:'6px 10px'}}>
          {availableLanguages.map(l=><option key={l.code} value={l.code}>{l.label}</option>)}
        </Select>
      </div>

      {!result ? (
        <TopGrid>
          <Card>
            <CardTitle>{t.birthDetails || 'Birth Details'}</CardTitle>
            <BirthDataForm
              fields={['name', 'gender', 'dob', 'time', 'place']}
              values={formValues}
              onChange={setFormValues}
              onSubmit={handleSubmit}
              submitLabel={loading ? (t.computing||'Computing...') : (t.computeBalance||'Compute Daily Balance')}
              loading={loading}
              error={error}
              pageName="dailyBalance"
              lang={lang}
            />
          </Card>
          <Card>
            <CardTitle>{t.whatIsKalaShakti || 'What is KalaShakti?'}</CardTitle>
            <p style={{color:'#555',lineHeight:1.7,fontSize:'0.95rem'}}>
              {t.kalaShaktiDesc || 'KalaShakti Daily Balance combines your birth chart\'s Ashtakavarga data with current planetary transits to give you a personalized daily score (0-100). It tells you WHAT is happening, WHY it affects you, WHERE in your life, WHEN the effect is strongest, and WHAT TO DO about it.'}
            </p>
            <div style={{marginTop:20}}>
              <h3 style={{fontSize:'1rem',color:'#333',marginBottom:10}}>{t.scoreComponents || 'Score Components'}</h3>
              <ComponentsBar>
                <ComponentChip bg="#e8f5e9" fg="#2e7d32">Transit Env (40)</ComponentChip>
                <ComponentChip bg="#e3f2fd" fg="#1565c0">Kakshya (30)</ComponentChip>
                <ComponentChip bg="#fff3e0" fg="#e65100">Moon State (20)</ComponentChip>
                <ComponentChip bg="#fce4ec" fg="#c62828">Aspect (10)</ComponentChip>
              </ComponentsBar>
            </div>
          </Card>
        </TopGrid>
      ) : (
        <>
          {/* Score + Summary */}
          <Card style={{marginBottom:20}}>
            <div style={{display:'flex',flexDirection:'column',alignItems:'center',margin:'20px 0'}}>
              <SharedScoreRing value={result.score} max={100} color={scoreColor} label={result.band.replace('_',' ')} />
            </div>
            <Summary>{result.summary}</Summary>
            <ComponentsBar>
              <ComponentChip bg="#e8f5e9" fg="#2e7d32">Transit: {result.components.transit_env.score}/{result.components.transit_env.max}</ComponentChip>
              <ComponentChip bg="#e3f2fd" fg="#1565c0">Kakshya: {result.components.kakshya.score}/{result.components.kakshya.max}</ComponentChip>
              <ComponentChip bg="#fff3e0" fg="#e65100">Moon: {result.components.moon_state.score}/{result.components.moon_state.max}</ComponentChip>
              <ComponentChip bg="#fce4ec" fg="#c62828">Aspect: {result.components.aspect_align.score}/{result.components.aspect_align.max}</ComponentChip>
            </ComponentsBar>
            <div style={{textAlign:'center'}}>
              <SubmitButton onClick={()=>{setResult(null);setHourly(null);setWeekly(null);setSelectedWeekDay(null);}} style={{width:'auto',padding:'8px 24px',fontSize:'0.85rem'}}>
                {t.newChart || 'New Chart'}
              </SubmitButton>
            </div>
          </Card>

          {/* Tab navigation */}
          <div style={{margin:'30px 0 20px'}}>
            <ResultTabs
              tabs={[
                {key:'highlights', label: t.tabHighlights||'Highlights'},
                {key:'planets', label: t.tabPlanets||'Planet Reports'},
                {key:'sky', label: t.tabSky||'Current Sky'},
                {key:'hourly', label: t.tabHourly||'Hourly Timeline'},
                {key:'weekly', label: t.tabWeekly||'Weekly Forecast'},
              ]}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {/* Highlights Tab */}
          {activeTab==='highlights' && result.highlights && (
            <div>
              <p style={{color:'#888',fontSize:'0.82rem',marginBottom:14,textAlign:'center',fontStyle:'italic'}}>
                {lang==='ta' ? 'இன்றைய மிக முக்கியமான கிரக தாக்கங்கள் — உங்கள் ஜாதகத்தின் அஷ்டகவர்க்க அடிப்படையில்' : 'Today\'s most significant planetary impacts — based on your birth chart\'s Ashtakavarga scores'}
              </p>
              {result.highlights.map((h, i) => (
                <HighlightCard key={i} type={h.type}>
                  <HighlightType type={h.type}>{h.type === 'strength' ? (t.strength||'Strength') : (t.caution||'Caution')}</HighlightType>
                  <HighlightHeadline>{h.planet} {h.planet_tamil ? `(${h.planet_tamil})` : ''} — {h.headline}</HighlightHeadline>
                  <HighlightDetail>{h.detail}</HighlightDetail>
                  <HighlightMeta>{t.lifeArea||'Life Area'}: {h.life_area} | BAV: {h.bav}/8 | SAV: {h.sav}</HighlightMeta>
                </HighlightCard>
              ))}
            </div>
          )}

          {/* Planet Reports Tab */}
          {activeTab==='planets' && result.planet_reports && (
            <>
            <p style={{color:'#888',fontSize:'0.82rem',marginBottom:14,textAlign:'center',fontStyle:'italic'}}>
              {lang==='ta' ? 'ஒவ்வொரு கிரகமும் இன்று உங்கள் வாழ்க்கையை எப்படி பாதிக்கிறது — BAV, வீடு, கக்ஷ்யை விவரங்களுடன்' : 'How each planet affects your life today — with BAV score, house placement, and kakshya timing'}
            </p>
            <ReportGrid>
              {result.planet_reports.map((r, i) => (
                <PlanetCard key={i} effectColor={r.effect_color||'#999'}>
                  <PlanetHeader>
                    <div><PlanetName>{r.planet}</PlanetName><PlanetTamil>{r.planet_tamil}</PlanetTamil></div>
                    <EffectBadge effect={r.effect}>{r.effect}</EffectBadge>
                  </PlanetHeader>
                  <PlanetRow><span>{t.transitSign||'Transit Sign'}</span><span>{r.transit_sign} ({r.transit_sign_tamil}) {r.transit_degree}°</span></PlanetRow>
                  <PlanetRow><span>BAV</span><span>{r.bav_score}/8 ({r.bav_quality})</span></PlanetRow>
                  <PlanetRow><span>SAV</span><span>{r.sav_score} ({r.sav_quality})</span></PlanetRow>
                  <PlanetRow><span>{t.house||'House'}</span><span>{r.house_number} — {r.house_label}</span></PlanetRow>
                  <PlanetRow><span>{t.kakshya||'Kakshya'}</span><span>{r.kakshya_lord} {r.kakshya_favorable ? '✓' : '✗'}</span></PlanetRow>
                  <PlanetSection>
                    <PlanetHeadline>{r.headline}</PlanetHeadline>
                    <PlanetGuidance>{r.guidance}</PlanetGuidance>
                  </PlanetSection>
                </PlanetCard>
              ))}
            </ReportGrid>
            </>
          )}

          {/* Current Sky Tab */}
          {activeTab==='sky' && result.current_sky && (
            <Card>
              <CardTitle>{t.currentSky||'Current Sky'}</CardTitle>
              <p style={{textAlign:'center',color:'#888',fontSize:'0.82rem',marginBottom:6,fontStyle:'italic'}}>
                {lang==='ta' ? 'இப்போது வானில் கிரகங்கள் எங்கே உள்ளன — ராசி, நட்சத்திரம், கக்ஷ்யை' : 'Where planets are right now — sign, nakshatra, and kakshya position'}
              </p>
              <p style={{textAlign:'center',color:'#aaa',fontSize:'0.75rem',marginBottom:14}}>{result.current_sky.timestamp}</p>
              <ResponsiveTable
                columns={[
                  {key:'planet',label:t.planet||'Planet'},
                  {key:'sign_display',label:t.sign||'Sign'},
                  {key:'degree_display',label:t.degree||'Degree'},
                  {key:'nakshatra_display',label:t.nakshatra||'Nakshatra'},
                  {key:'kakshya',label:t.kakshya||'Kakshya'},
                ]}
                rows={result.current_sky.planets.map(p => ({
                  ...p,
                  sign_display: `${p.sign} (${p.sign_tamil})`,
                  degree_display: `${p.degree}°${p.retrograde ? ' (R)' : ''}`,
                  nakshatra_display: `${p.nakshatra} P${p.pada}`,
                }))}
              />
            </Card>
          )}

          {/* Hourly Timeline Tab */}
          {activeTab==='hourly' && hourly && (
            <Card>
              <CardTitle>{t.hourlyTimeline||'Hourly Timeline'} — {hourly.date}</CardTitle>
              <p style={{color:'#888',fontSize:'0.82rem',marginBottom:14,textAlign:'center',fontStyle:'italic'}}>
                {lang==='ta' ? 'ஒவ்வொரு மணி நேரமும் கக்ஷ்யை அடிப்படையில் எப்படி மாறுகிறது — சிறந்த நேரத்தை கண்டறியுங்கள்' : 'Hour-by-hour energy based on kakshya timing — find your best window for action'}
              </p>

              {/* Daily Projection */}
              {hourly.daily_projection && (
                <div style={{marginBottom:20}}>
                  <p style={{textAlign:'center',color:'#555',fontSize:'0.9rem',marginBottom:12}}>{hourly.daily_projection.summary}</p>
                  <ProjectionGrid>
                    {['morning','afternoon','evening'].map(period => {
                      const pd = hourly.daily_projection[period];
                      return pd ? (
                        <PeriodCard key={period} quality={pd.quality}>
                          <PeriodLabel>{period.charAt(0).toUpperCase()+period.slice(1)}</PeriodLabel>
                          <PeriodScore quality={pd.quality}>{pd.score}/30</PeriodScore>
                          <PeriodQuality>{pd.quality}</PeriodQuality>
                          {pd.narrative && <div style={{fontSize:'0.75rem',color:'#555',marginTop:6,fontStyle:'italic'}}>{pd.narrative}</div>}
                        </PeriodCard>
                      ) : null;
                    })}
                  </ProjectionGrid>
                </div>
              )}

              {hourly.best_window && (
                <div style={{textAlign:'center',padding:'10px',background:'#e8f5e9',borderRadius:8,marginBottom:16}}>
                  <strong>{t.bestWindow||'Best Window'}:</strong> {String(hourly.best_window.start_hour).padStart(2,'0')}:00 — {String(hourly.best_window.end_hour).padStart(2,'0')}:00 (Score: {hourly.best_window.score}/30)
                </div>
              )}
              <TimelineContainer>
                {hourly.hours && hourly.hours.map((h, i) => {
                  const isBest = hourly.best_window && i >= hourly.best_window.start_hour && i < hourly.best_window.end_hour;
                  const pct = Math.round((h.kakshya_score / 30) * 100);
                  const barColor = pct >= 70 ? '#4caf50' : pct >= 40 ? '#ffc107' : '#f44336';
                  const isExpanded = expandedHour === i;
                  return (
                    <React.Fragment key={i}>
                      <TimelineRow isBest={isBest} onClick={()=>setExpandedHour(isExpanded?null:i)}>
                        <span style={{fontSize:'0.85rem',fontWeight:600}}>{h.label}</span>
                        <TimelineBar color={barColor} pct={pct} />
                        <span style={{fontSize:'0.8rem',color:'#666',textAlign:'right'}}>{h.kakshya_score}/30</span>
                      </TimelineRow>
                      {h.events && h.events.length > 0 && h.events.map((ev, j) => (
                        <TimelineEvent key={`${i}-${j}`}>{ev}</TimelineEvent>
                      ))}
                      {isExpanded && (
                        <TimelineDetail>
                          <div style={{marginBottom:6}}>
                            <strong>{h.favorable_count}/7 favorable</strong> — {h.interpretation}
                          </div>
                          {h.planets && (
                            <div style={{marginBottom:6}}>
                              {h.planets.map((p, pi) => (
                                <PlanetPill key={pi} effect={p.effect}>
                                  {p.planet} {p.bav}/8 · {p.sign} · H{p.house} ({p.house_area}) {p.kakshya_favorable ? '✓' : '✗'}
                                </PlanetPill>
                              ))}
                            </div>
                          )}
                          <em>{h.advice}</em>
                          <br/><span style={{color:'#999',fontSize:'0.75rem'}}>Moon in {h.moon_sign} ({h.moon_sign_tamil})</span>
                        </TimelineDetail>
                      )}
                    </React.Fragment>
                  );
                })}
              </TimelineContainer>
            </Card>
          )}

          {/* Weekly Forecast Tab */}
          {activeTab==='weekly' && weekly && (
            <div>
              <p style={{color:'#888',fontSize:'0.82rem',marginBottom:14,textAlign:'center',fontStyle:'italic'}}>
                {lang==='ta' ? 'அடுத்த 7 நாட்களின் கலாசக்தி மதிப்பெண் — சிறந்த மற்றும் கடினமான நாட்களை அறிந்து திட்டமிடுங்கள்' : 'Your KalaShakti score for the next 7 days — plan ahead by knowing your best and toughest days'}
              </p>
              {/* Weekly Summary */}
              {weekly.weekly_summary && (
                <Card style={{marginBottom:20}}>
                  <CardTitle>{t.weeklyForecast||'7-Day Forecast'}</CardTitle>
                  <p style={{textAlign:'center',color:'#555',fontSize:'0.95rem',marginBottom:12}}>{weekly.weekly_summary.summary}</p>
                  <ComponentsBar>
                    <ComponentChip bg="#e8f5e9" fg="#2e7d32">Avg: {weekly.weekly_summary.average_score}/100</ComponentChip>
                    <ComponentChip bg="#e3f2fd" fg="#1565c0">Best: {weekly.weekly_summary.best_day}</ComponentChip>
                    <ComponentChip bg="#fce4ec" fg="#c62828">Watch: {weekly.weekly_summary.worst_day}</ComponentChip>
                  </ComponentsBar>
                  {weekly.weekly_summary.narrative && (
                    <p style={{fontSize:'0.85rem',color:'#555',lineHeight:1.6,marginTop:12,padding:'0 10px'}}>{weekly.weekly_summary.narrative}</p>
                  )}
                </Card>
              )}

              {/* Day cards */}
              <WeekGrid>
                {weekly.days && weekly.days.map((day, i) => {
                  const dayColor = day.color || '#999';
                  return (
                    <DayCard key={i} color={dayColor} selected={selectedWeekDay===i} onClick={()=>setSelectedWeekDay(selectedWeekDay===i?null:i)}>
                      <DayDate>{day.date}</DayDate>
                      <DayName>{day.day_of_week.slice(0,3)}</DayName>
                      <DayScore color={dayColor}>{day.score}</DayScore>
                      <DayBand>{day.band.replace('_',' ')}</DayBand>
                      <DayMoon>{day.moon_sign}</DayMoon>
                    </DayCard>
                  );
                })}
              </WeekGrid>

              {/* Selected day detail */}
              {selectedWeekDay !== null && weekly.days[selectedWeekDay] && (
                <Card style={{marginTop:16}}>
                  <CardTitle>{weekly.days[selectedWeekDay].day_of_week} — {weekly.days[selectedWeekDay].date}</CardTitle>
                  <Summary>{weekly.days[selectedWeekDay].summary}</Summary>
                  {weekly.days[selectedWeekDay].forecast && (
                    <p style={{fontSize:'0.88rem',color:'#444',lineHeight:1.6,padding:'0 8px',marginBottom:16}}>
                      {weekly.days[selectedWeekDay].forecast}
                    </p>
                  )}
                  {/* Planet positions */}
                  {weekly.days[selectedWeekDay].planet_positions && (
                    <div style={{marginBottom:16}}>
                      {weekly.days[selectedWeekDay].planet_positions.map((p, pi) => (
                        <PlanetPill key={pi} effect={p.effect}>
                          {p.planet} {p.bav}/8 · {p.sign} · H{p.house} ({p.house_area})
                        </PlanetPill>
                      ))}
                    </div>
                  )}
                  {weekly.days[selectedWeekDay].highlights && weekly.days[selectedWeekDay].highlights.map((h, i) => (
                    <HighlightCard key={i} type={h.type}>
                      <HighlightType type={h.type}>{h.type === 'strength' ? 'Strength' : 'Caution'}</HighlightType>
                      <HighlightHeadline>{h.planet} {h.planet_tamil ? `(${h.planet_tamil})` : ''} — {h.headline}</HighlightHeadline>
                      <HighlightDetail>{h.detail}</HighlightDetail>
                      <HighlightMeta>Life Area: {h.life_area} | BAV: {h.bav}/8 | SAV: {h.sav}</HighlightMeta>
                    </HighlightCard>
                  ))}
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default DailyBalance;
